import type { CreateFoldersResult, FolderImportPreview } from '@st/sdk'
import { formatFolderEntityName } from '@features/st-folder-viewer/st-folder-display'
import { Button, CheckIcon, Spinner, Wizard } from '@st/theme'
import { keyBy } from '@st/util/array'
import { inflect } from '@st/util/inflect'
import { match, P } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { selDisabledReason, selFailureMessage, type ImportFailure } from './folder-import-state'
import {
  selAllJobsSelected,
  RunFolderImportMessage,
  RunFolderImportState
} from './run-folder-import-module'
import { TaxReturnSelectionTable, type TaxReturnCandidate } from './tax-return-selection-table'

type Props = {
  onClose: () => void
}
export function RunFolderImportPage({ onClose }: Props) {
  const folderImport = useProcess('runFolderImport')
  const state = useSelect('runFolderImport', (s) => s)

  return match(state)
    .with({ step: P.union('idle', 'finishing') }, () => null)
    .with({ step: 'loading' }, (state) => (
      <div className="min-w-[1200px] bg-red-100">
        <ProcessingPage onClose={onClose} status={state.importState?.status} />
      </div>
    ))
    .with({ step: 'loadFailed' }, () => (
      <div className="min-w-[1200px] bg-red-100">
        <LoadFailedPage send={folderImport.send} onClose={onClose} />
      </div>
    ))
    .with({ step: P.union('selectFolders', 'running', 'createFailed', 'completed') }, (state) => (
      <div className="min-w-[1200px] bg-red-100">
        <RunFolderImportContent state={state} send={folderImport.send} onClose={onClose} />
      </div>
    ))
    .exhaustive()
}

function ProcessingPage({
  onClose,
  status
}: {
  onClose: () => void
  status?: FolderImportPreview['status']
}) {
  return (
    <Wizard.Scaffold breadcrumb="Add clients" onClose={onClose}>
      <div className="flex h-full w-full flex-col items-center justify-center gap-6">
        <div role="status">
          <Spinner className="h-16 w-16 fill-blue-600 text-gray-200 dark:text-gray-600" />
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className="text-center text-gray-700">
            {match(status)
              .with('fetching', () => 'Fetching tax returns from ProConnect…')
              .otherwise(() => 'Processing...')}
          </div>
          <div className="text-center text-sm text-gray-700">
            This may take 2-3 minutes to complete.
          </div>
        </div>
      </div>
    </Wizard.Scaffold>
  )
}

function LoadFailedPage({
  send,
  onClose
}: {
  send: (message: RunFolderImportMessage) => void
  onClose: () => void
}) {
  return (
    <Wizard.Scaffold
      breadcrumb="Add clients"
      onClose={onClose}
      bottomNav={
        <Wizard.BottomNav>
          <div></div>
          <Button variant="primary" onClick={() => send({ type: 'retryLoad' })}>
            Retry
          </Button>
        </Wizard.BottomNav>
      }
    >
      <Wizard.Main variant="wide">
        <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
          <div className="text-lg font-semibold text-gray-900">We couldn't load this import.</div>
          <div className="text-gray-700">Try again or close this window.</div>
        </div>
      </Wizard.Main>
    </Wizard.Scaffold>
  )
}

function RunFolderImportContent({
  state,
  send,
  onClose
}: {
  state: Extract<
    RunFolderImportState,
    { step: 'selectFolders' | 'running' | 'createFailed' | 'completed' }
  >
  send: (message: RunFolderImportMessage) => void
  onClose: () => void
}) {
  const jobs = state.importState.folders
  const failedRecords = state.importState.failedRecords
  const selectedJobIds = new Set(state.selectedJobIds)
  const allJobIdsSelected = selAllJobsSelected(state)

  return (
    <Wizard.Scaffold
      onClose={onClose}
      bottomNav={
        <Wizard.BottomNav>
          <div></div>
          {match(state)
            .with({ step: 'selectFolders' }, () => (
              <Button
                variant="primary"
                disabled={selectedJobIds.size == 0}
                onClick={() => send({ type: 'runImport' })}
              >
                Import {selectedJobIds.size} {inflect(selectedJobIds.size, 'binder')}
              </Button>
            ))
            .with({ step: 'running' }, () => (
              <Button variant="primary" disabled={true}>
                Importing...
              </Button>
            ))
            .with({ step: 'createFailed' }, () => (
              <Button variant="primary" onClick={onClose}>
                Close
              </Button>
            ))
            .with({ step: 'completed' }, () => (
              <Button
                variant="primary"
                onClick={onClose}
                leadingIcon={<CheckIcon className="h-4 w-4" />}
              >
                Done
              </Button>
            ))
            .exhaustive()}
        </Wizard.BottomNav>
      }
    >
      {match(state)
        .with(
          {
            step: 'selectFolders',
            importState: { error: { message: P.string } }
          },
          ({ importState }) => {
            return (
              <Wizard.Main variant="wide">
                <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
                  <div className="flex h-full flex-col items-center justify-center gap-4 whitespace-pre text-center">
                    {importState.error.message}
                  </div>
                </div>
              </Wizard.Main>
            )
          }
        )
        // We only show this error message if nothing was extracted.
        .with(
          { step: 'selectFolders', importState: { folders: [], failedRecords: [] } },
          ({ importId }) => {
            return (
              <Wizard.Main variant="wide">
                <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
                  <div className="text-lg font-semibold text-gray-900">
                    No clients were found in the upload.
                    <br />
                    {`Please contact us at support@stanfordtax.com with the following reference: ${importId}`}
                  </div>
                </div>
              </Wizard.Main>
            )
          }
        )
        .with({ step: 'selectFolders' }, (state) => {
          return (
            <Wizard.Main
              variant="wide"
              title="Generate binders"
              subtitle="Below are the clients that were in the backup file. Click the import button to generate their binders."
            >
              <ImportFoldersTable
                jobs={jobs}
                failedRecords={failedRecords}
                step={state.step}
                selectedJobIds={selectedJobIds}
                allJobIdsSelected={allJobIdsSelected}
                send={send}
              />
            </Wizard.Main>
          )
        })
        .with({ step: 'running' }, () => {
          return (
            <Wizard.Main variant="wide">
              <GeneratingChecklistsProcessing />
            </Wizard.Main>
          )
        })
        .with({ step: 'createFailed' }, () => (
          <Wizard.Main variant="wide">
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
              <div className="text-lg font-semibold text-gray-900">
                We couldn't confirm that your binders were generated.
              </div>
              <div className="max-w-xl text-gray-700">
                Close this window and check your binder list before attempting the import again.
              </div>
            </div>
          </Wizard.Main>
        ))
        .with({ step: 'completed' }, (state) => {
          const successfulImportCount = state.createFoldersResult.results.length

          return (
            <Wizard.Main
              variant="wide"
              title={`You successfully generated ${successfulImportCount} binders`}
              subtitle="You can preview the binders for each of the clients on the main page"
            >
              <ImportFoldersTable
                jobs={state.importState.folders}
                failedRecords={state.importState.failedRecords}
                step={state.step}
                selectedJobIds={selectedJobIds}
                allJobIdsSelected={allJobIdsSelected}
                createFoldersResult={state.createFoldersResult}
                send={send}
              />
            </Wizard.Main>
          )
        })
        .exhaustive()}
    </Wizard.Scaffold>
  )
}

function ImportFoldersTable({
  step,
  jobs,
  failedRecords,
  selectedJobIds,
  allJobIdsSelected,
  createFoldersResult,
  send
}: {
  jobs: FolderImportPreview['folders']
  failedRecords: ImportFailure[]
  selectedJobIds: Set<string>
  createFoldersResult?: CreateFoldersResult
  allJobIdsSelected: boolean
  send: (message: RunFolderImportMessage) => void
  step: 'selectFolders' | 'completed'
}) {
  const resultsByJobId = keyBy(createFoldersResult?.results ?? [], (result) => result.taxReturnId)
  const candidates = jobs.map((job) => taxReturnCandidate(job, resultsByJobId[job.taxReturnId]))
  const selection = match(step)
    .with('selectFolders', () => ({
      status: 'editable' as const,
      selectedCandidateIds: selectedJobIds,
      allCandidatesSelected: allJobIdsSelected,
      onToggleAll: () => send({ type: 'toggleSelectAll' as const }),
      onToggleCandidate: (jobId: string) => send({ type: 'toggleJobSelected' as const, jobId })
    }))
    .with('completed', () => ({ status: 'hidden' as const }))
    .exhaustive()

  return (
    <TaxReturnSelectionTable
      candidates={candidates}
      failures={failedRecords.map((failure) => ({
        ...failure,
        message: selFailureMessage(failure)
      }))}
      selection={selection}
    />
  )
}

function taxReturnCandidate(
  job: FolderImportPreview['folders'][number],
  result?: CreateFoldersResult['results'][number]
): TaxReturnCandidate {
  let status: TaxReturnCandidate['status']
  if (result) status = { label: 'Success', color: 'green' }

  if (!result && job.existingFolderId != null) {
    status = { label: 'Already imported', color: 'gray' }
  }

  return {
    id: job.taxReturnId,
    name: formatFolderEntityName(job.entities),
    email: job.entities.taxpayer?.email,
    spouseEmail: job.entities.spouse?.email,
    returnType: job.returnType,
    taxYear: job.year,
    sourceProviderId: job.sourceProviderId,
    disabledReason: selDisabledReason(job),
    status
  }
}

function GeneratingChecklistsProcessing() {
  return (
    <div className="flex w-[600px] flex-1 flex-col items-center justify-center gap-2">
      <div role="status">
        <Spinner className="h-16 w-16 fill-blue-600 text-gray-200 dark:text-gray-600" />
      </div>

      <h3 className="mt-4 text-center text-2xl font-semibold">
        StanfordTax is generating binders for your clients using their prior year information.
      </h3>
    </div>
  )
}
